import React from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Download,
  Shield,
  Clock,
  Users,
  TrendingUp,
  Calendar,
  DollarSign,
  CheckCircle,
  Zap,
} from "lucide-react";
import AnimatedGradientText from "./AnimatedGradientText";
import { Link } from "react-router-dom";

// Enhanced Technical Animation with more relevant info
const TechnicalAnimation = () => (
  <div className="relative w-full h-[500px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 overflow-hidden shadow-2xl border border-slate-700">
    {/* Glowing effect */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 blur-3xl rounded-full"></div>

    {/* Glass card - How It Works */}
    <div className="absolute top-6 left-6 w-72 backdrop-blur-md bg-white/5 rounded-xl p-4 border border-white/10 shadow-xl">
      <h3 className="text-lg font-sans font-semibold text-white mb-2 flex items-center">
        <Zap size={18} className="mr-2 text-blue-400" />
        How Digital Committees Work
      </h3>
      <ul className="space-y-2 text-sm font-sans text-blue-100">
        <li className="flex items-start">
          <span className="bg-blue-500/20 p-1 rounded-full mr-2 mt-0.5">
            <CheckCircle size={12} className="text-blue-400" />
          </span>
          <span>Join with verified members and set monthly contribution</span>
        </li>
        <li className="flex items-start">
          <span className="bg-blue-500/20 p-1 rounded-full mr-2 mt-0.5">
            <CheckCircle size={12} className="text-blue-400" />
          </span>
          <span>Each month one member receives the full pool amount</span>
        </li>
        <li className="flex items-start">
          <span className="bg-blue-500/20 p-1 rounded-full mr-2 mt-0.5">
            <CheckCircle size={12} className="text-blue-400" />
          </span>
          <span>Continue until everyone has received their payout</span>
        </li>
      </ul>
    </div>

    {/* Glass cards - Key Benefits */}
    <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-3 justify-center">
      <div className="backdrop-blur-md bg-white/5 rounded-xl p-3 border border-white/10 shadow-xl flex items-center w-48">
        <div className="p-2 rounded-full bg-green-500/20 mr-3">
          <DollarSign size={16} className="text-green-400" />
        </div>
        <div>
          <h4 className="text-sm font-sans font-medium text-white">
            No Interest Loans
          </h4>
          <p className="text-xs font-sans text-green-200">
            Access funds when needed
          </p>
        </div>
      </div>

      <div className="backdrop-blur-md bg-white/5 rounded-xl p-3 border border-white/10 shadow-xl flex items-center w-48">
        <div className="p-2 rounded-full bg-purple-500/20 mr-3">
          <Users size={16} className="text-purple-400" />
        </div>
        <div>
          <h4 className="text-sm font-sans font-medium text-white">
            Community Savings
          </h4>
          <p className="text-xs font-sans text-purple-200">Stronger together</p>
        </div>
      </div>

      <div className="backdrop-blur-md bg-white/5 rounded-xl p-3 border border-white/10 shadow-xl flex items-center w-48">
        <div className="p-2 rounded-full bg-yellow-500/20 mr-3">
          <Calendar size={16} className="text-yellow-400" />
        </div>
        <div>
          <h4 className="text-sm font-sans font-medium text-white">
            Flexible Terms
          </h4>
          <p className="text-xs font-sans text-yellow-200">3-24 month cycles</p>
        </div>
      </div>

      <div className="backdrop-blur-md bg-white/5 rounded-xl p-3 border border-white/10 shadow-xl flex items-center w-48">
        <div className="p-2 rounded-full bg-red-500/20 mr-3">
          <TrendingUp size={16} className="text-red-400" />
        </div>
        <div>
          <h4 className="text-sm font-sans font-medium text-white">
            Grow Your Money
          </h4>
          <p className="text-xs font-sans text-red-200">
            Better than bank savings
          </p>
        </div>
      </div>
    </div>

    {/* Connecting lines */}
    <svg
      className="absolute inset-0 w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100,100 C150,150 200,50 250,120"
        stroke="rgba(59, 130, 246, 0.3)"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M150,200 C200,250 300,220 350,180"
        stroke="rgba(147, 51, 234, 0.3)"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M250,300 C300,320 350,280 400,350"
        stroke="rgba(236, 72, 153, 0.3)"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M50,250 C100,300 150,350 200,380"
        stroke="rgba(37, 99, 235, 0.3)"
        strokeWidth="1"
        fill="none"
      />
    </svg>

    {/* Security grid pattern */}
    <div className="absolute inset-0 grid grid-cols-12 grid-rows-12 gap-4 opacity-20">
      {[...Array(144)].map((_, i) => (
        <div key={i} className="border border-white/10 rounded-sm" />
      ))}
    </div>
  </div>
);

const Hero = () => {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden font-sans">
      {/* Background decorations */}

      <div className="container px-4 mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text content */}
          <div className="max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
            {/* Status badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-5">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 border-2 border-amber-400 text-white text-sm font-bold shadow-lg transform hover:scale-105 transition-all duration-300">
                <Clock size={16} className="mr-2 animate-pulse" />
                Launching Soon!
              </div>

              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-medium">
                <Shield size={14} className="mr-1.5" />
                100% Secure & Verified
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold tracking-tight leading-none mb-6 bg-clip-text">
              Modern{" "}
              <AnimatedGradientText as="span" className="inline font-sans">
                Digital Committee
              </AnimatedGradientText>{" "}
              <span className="font-sans">Simplified</span>
            </h1>

            <p className="text-lg text-gray-700 mb-4 max-w-xl mx-auto lg:mx-0">
              Think of it like a savings club with your friends - you all put in
              money each month, and every month one person gets everyone's
              contribution. It's like your traditional savings group, but done
              online with strong security.
            </p>

            <p className="text-base text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Perfect for saving for big purchases, emergencies, or investments
              without high-interest loans or complex application processes.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-3 sm:space-y-0 sm:space-x-4 mt-8">
              <Button
                size="lg"
                className="w-full sm:w-auto text-base font-medium shadow-lg group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 rounded-xl transition-all duration-300"
                asChild
              >
                <Link
                  to="/waitlist"
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <Download size={18} className="mr-2" />
                  Join Waitlist
                  <ArrowRight
                    size={16}
                    className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-base font-medium border-2 border-gray-200 text-gray-700 hover:bg-gray-50 px-8 py-6 rounded-xl transition-all duration-300"
                asChild
              >
                <Link to="/learn-more" className="flex items-center">
                  Learn More
                </Link>
              </Button>
            </div>
          </div>

          {/* Enhanced Technical Animation */}
          <div className="hidden lg:block lg:transform lg:translate-x-4">
            <TechnicalAnimation />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
